import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { QualificationBadge } from '../components/ui/Badge';
import { TripDetailModal } from '../components/trips/TripDetailModal';
import { usersApi } from '../api/users.api';
import { tripsApi } from '../api/trips.api';
import { User, UserRole } from '../types/user';
import { Trip } from '../types/trip';
import { useAuth } from '../hooks/useAuth';
import { formatDate, formatTime, formatDuration, formatDistance } from '../utils/formatters';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const roleColors: Record<string, string> = {
  super_admin: 'bg-purple-100 text-purple-800',
  company_admin: 'bg-blue-100 text-blue-800',
  driver: 'bg-green-100 text-green-800',
  hr: 'bg-pink-100 text-pink-800',
  accounting: 'bg-yellow-100 text-yellow-800',
  manager: 'bg-indigo-100 text-indigo-800',
};

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

// ---------------------------------------------------------------------------
// Info field helper
// ---------------------------------------------------------------------------

const InfoField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg px-4 py-3">
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-900">{value}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  const isSuperAdmin = currentUser?.isSuperAdmin === true || currentUser?.role === 'super_admin';
  const canViewGps =
    isSuperAdmin ||
    currentUser?.role === 'driver' ||
    currentUser?.company?.gps_data_visible_to_company_users === true;

  const [userData, setUserData] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState<string | null>(null);

  const [trips, setTrips] = useState<Trip[]>([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const LIMIT = 10;

  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);

  const loadUser = useCallback(async () => {
    if (!id) return;
    setUserLoading(true);
    setUserError(null);
    try {
      const data = await usersApi.getUser(id);
      setUserData(data);
    } catch {
      setUserError('Impossible de charger cet utilisateur.');
    } finally {
      setUserLoading(false);
    }
  }, [id]);

  const loadTrips = useCallback(async () => {
    if (!id || !userData || userData.role !== 'driver') return;
    setTripsLoading(true);
    try {
      const result = await tripsApi.getTrips({ driverId: id, page, limit: LIMIT });
      setTrips(Array.isArray(result.data) ? result.data : []);
      setTotalPages(result.pagination?.totalPages ?? 1);
    } catch {
      setTrips([]);
    } finally {
      setTripsLoading(false);
    }
  }, [id, userData, page]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (userData) loadTrips();
  }, [loadTrips, userData]);

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const getRoleLabel = (role: UserRole): string => t(`users.roles.${role}`);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Retour
        </button>

        {/* User info card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {userLoading ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-14" />
                ))}
              </div>
            </div>
          ) : userError ? (
            <div className="text-center py-8">
              <p className="text-red-600">{userError}</p>
              <button
                onClick={loadUser}
                className="mt-3 text-sm text-primary-600 hover:underline"
              >
                Réessayer
              </button>
            </div>
          ) : userData ? (
            <div>
              {/* Avatar + name */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-700 font-bold text-xl">
                    {getInitials(userData.name)}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900">{userData.name}</h1>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${roleColors[userData.role] ?? 'bg-gray-100 text-gray-700'}`}
                    >
                      {getRoleLabel(userData.role)}
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        userData.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {userData.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <p className="text-gray-500 mt-1">{userData.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoField label="Email" value={userData.email} />
                <InfoField label="Téléphone" value={userData.phone || '—'} />
                <InfoField label="Rôle" value={getRoleLabel(userData.role)} />
                <InfoField
                  label="Statut"
                  value={userData.status === 'active' ? 'Actif' : 'Inactif'}
                />
                <InfoField
                  label="Membre depuis"
                  value={formatDate(userData.createdAt)}
                />
                {userData.company && (
                  <InfoField label="Société" value={userData.company.name} />
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Trips section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Trajets</h2>
          </div>

          {userLoading ? (
            <div className="p-6 space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : userData && userData.role !== 'driver' ? (
            <div className="p-12 text-center">
              <div className="text-5xl mb-3">👤</div>
              <p className="text-gray-500">Cet utilisateur n'est pas un conducteur</p>
            </div>
          ) : tripsLoading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : trips.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-5xl mb-3">🗺️</div>
              <p className="text-gray-500">Aucun trajet pour ce conducteur</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durée
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Distance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qualification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trips.map((trip) => (
                    <tr key={trip._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatDate(trip.start_time)}
                        </div>
                        <div className="text-xs text-gray-500">{formatTime(trip.start_time)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDuration(trip.duration_minutes)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDistance(trip.distance_km)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <QualificationBadge type={trip.qualification} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedTrip(trip)}
                          className="text-gray-400 hover:text-primary-600 transition-colors"
                          title="Voir le détail"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!tripsLoading && !userLoading && userData?.role === 'driver' && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} sur {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Précédent
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trip detail modal */}
      {selectedTrip && (
        <TripDetailModal
          trip={selectedTrip}
          canViewGps={canViewGps}
          onClose={() => setSelectedTrip(null)}
        />
      )}
    </Layout>
  );
};
